import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import useCruiseOfferView from 'hooks/Cruise/useCruiseOfferView'
import { isCruiseOffer } from 'lib/offer/offerTypes'
import { useCallback, useContext, useMemo, type MouseEvent } from 'react'
import { useIntl } from 'react-intl'
import BookmarkButton from 'wishlists/components/Bookmark/Buttons'
import type { SearchTileLogo } from '../SearchTile'
import SearchTile from '../SearchTile'
import SearchTileRowOverlineJourney from '../shared/SearchTileRowOverlineJourney'
import CruiseSearchTileFeatures from './CruiseSearchTileFeatures'
import CruiseSearchTileLabels from './CruiseSearchTileLabels'
import CruiseSearchTileMeta from './CruiseSearchTileMeta'
import CruiseSearchTilePriceStack from './CruiseSearchTilePriceStack'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import useCruiseSelectedCabinType from 'hooks/useCruiseSelectedCabinType'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import { trimCruiseShipNameFromTitle } from 'lib/cruises/cruiseUtils'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import { hasAnyBundleFlightCredits } from 'lib/cruises/cruiseBundleFlightCredits'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import ModalContext from 'contexts/ModalContext'
import CruiseOfferTileInclusionsModal from './CruiseOfferTileInclusionsModal'
import { getCheapestCruiseFlashOfferByCabin } from 'lib/cruises/getCheapestCruiseFlashOfferByCabin'
import { hasDisplayablePackagePrice, isDisplayablePrice } from 'lib/offer/pricingVisibility'

const CRUISE_BUNDLE_HOTEL_ITEM_TYPES = new Set(['PRE_ACCOMMODATION', 'POST_ACCOMMODATION'])

function bundleHasCruiseHotelItems(bundle: App.CruiseOfferBundle): boolean {
  return bundle.items.some((item) => CRUISE_BUNDLE_HOTEL_ITEM_TYPES.has(item.itemType))
}

interface Props {
  offer: App.CruiseOffer | App.TourOffer | App.TourOfferSummary
  filters?: App.OfferListFilters
}

function CruiseOfferTile({ offer, filters }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const selectedCabinType = useCruiseSelectedCabinType(isCruiseOffer(offer) ? offer.cruiseType : undefined)
  const view = useCruiseOfferView({ offer, cabinTypes: selectedCabinType || undefined, filters })

  const logo = useMemo((): SearchTileLogo => ({
    compact: { id: view.cruiseLineImage, title: view.cruiseLineName },
    full: { id: view.cruiseLineImage, title: view.cruiseLineName },
  }), [view])

  const title = useMemo(() => trimCruiseShipNameFromTitle(offer.name), [offer.name])

  const offerSiteTakeover = useCurrentSiteTakeover({ offer })
  const packagePricing = isCruiseOffer(offer) ? undefined : getCheapestCruiseFlashOfferByCabin({ offer, cabinType: selectedCabinType || '' }) || offer.lowestPricePackage
  const showPricingBlock = isCruiseOffer(offer) ?
      isDisplayablePrice(view.lowestPrice.promotionalPrice?.total ?? view.lowestPrice.primaryPrice.total) :
      hasDisplayablePackagePrice(packagePricing)
  const viewOfferLabel = intl.formatMessage({
    id: '1TDNDgzK3pnQZCGqQ7VW',
    defaultMessage: 'View offer',
    description: 'Button text to view cruise offer details in the cruise section',
  })
  const openBundleInclusionsModal = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()

    if (!isCruiseOffer(offer)) {
      return
    }

    showModal(<CruiseOfferTileInclusionsModal
      offer={offer}
      evergreenInclusions={view.evergreenInclusions}
      standardInclusions={view.standardInclusions}
      luxPlusInclusions={view.luxPlusInclusions}
    />)
  }, [offer, showModal, view.evergreenInclusions, view.luxPlusInclusions, view.standardInclusions])

  const productLabel = useMemo(() => {
    const typeLabel = <ProductTypeLabel productType={offer.productType} />
    if (!isCruiseOffer(offer) || !offer.bundle?.rateCodes.length) {
      return typeLabel
    }
    const hasBundleHotels = bundleHasCruiseHotelItems(offer.bundle)
    const hasBundleFlightCredits = hasAnyBundleFlightCredits(offer.bundle)

    if (!hasBundleHotels && !hasBundleFlightCredits) {
      return typeLabel
    }

    let bundleTitle = intl.formatMessage({
      id: 'YTJIZPQgudsTc34xUMg_',
      defaultMessage: 'Cruise + Hotel',
      description: 'Tag label for cruise booking that includes hotel accommodation',
    })

    if (hasBundleHotels && hasBundleFlightCredits) {
      bundleTitle = intl.formatMessage({
        id: 'gkCGFU8iEqY1ZySPPxCZ',
        defaultMessage: 'Cruise + Hotel + Flight credits',
        description:
          'Search tile promo label when a cruise bundle includes both hotel accommodation and flight credits.',
      })
    }

    if (!hasBundleHotels && hasBundleFlightCredits) {
      bundleTitle = intl.formatMessage({
        id: 'Vsqk2aCOOtcfvLCrfwef',
        defaultMessage: 'Cruise + Flight credits',
        description: 'Search tile promo label when a cruise bundle includes flight credits but no hotel items.',
      })
    }

    return <LabelGroup>
      <PromotionLabel icon={<LineShipIcon aria-hidden />}>
        <Group direction="horizontal" gap={4} verticalAlign="center">
          {bundleTitle}
          <IconButton
            kind="tertiary"
            variant="ghost"
            size="xsmall"
            shape="circle"
            onClick={openBundleInclusionsModal}
            aria-label={intl.formatMessage({
              id: 'RdCZpvczcWSuirV9basT',
              defaultMessage: 'View cruise inclusions',
              description: 'Accessible label for cruise bundled offer inclusions info button in search results',
            })}
          >
            <LineInfoCircleIcon colour="normal-contrast" aria-hidden />
          </IconButton>
        </Group>
      </PromotionLabel>
      {typeLabel}
    </LabelGroup>
  }, [offer, intl, openBundleInclusionsModal])

  return <SearchTile
    banner={<>{offerSiteTakeover?.searchPage?.tileBanner}</>}
    action={<TextButton nonInteractable kind="primary">{viewOfferLabel}</TextButton>}
    bookmarkAction={<BookmarkButton offer={offer} />}
    features={<CruiseSearchTileFeatures view={view} filters={filters} />}
    images={offer.images}
    labels={<CruiseSearchTileLabels view={view} />}
    logo={logo}
    meta={<CruiseSearchTileMeta view={view} />}
    overline={<SearchTileRowOverlineJourney start={view.departureCity} end={view.destinationCity} />}
    pricePoint={showPricingBlock && <CruiseSearchTilePriceStack view={view} />}
    productLabel={productLabel}
    title={title}
    offer={offer}
  />
}

export default CruiseOfferTile
